<template>
  <v-app>
    <v-main>
      <!-- Hero Section -->
      <!-- Hero Section -->
      <section class="hero-section">
        <!-- Floating geometric shapes -->
        <div class="floating-shapes">
          <div class="shape shape-1"></div>
          <div class="shape shape-2"></div>
          <div class="shape shape-3"></div>
          <div class="shape shape-4"></div>
        </div>

        <v-container class="hero-container fill-height">
          <v-row align="center" justify="space-between" class="fill-height">
            <!-- Text Content -->
            <v-col cols="12" md="7" lg="6" class="hero-text-col">
              <div class="hero-content text-left">
                <h1 class="hero-title font-weight-bold mb-4">
                  <span class="text-white">Bailey</span>
                  <span class="text-gradient"> Russo</span>
                </h1>

                <h2 class="hero-subtitle mb-8">
                  Full Stack Developer & <br />
                  <span class="highlight">Creative Technologist</span>
                </h2>

                <p class="hero-description mb-10">
                  I craft digital experiences that blend technical excellence
                  with
                  <span class="text-white font-weight-medium"
                    >innovative design</span
                  >. Specializing in building scalable applications that solve
                  real-world problems.
                </p>

                <div class="action-buttons d-flex align-center flex-wrap gap-4">
                  <v-btn
                    color="primary"
                    size="x-large"
                    class="hero-btn-primary rounded-pill px-8"
                    elevation="8"
                    @click="scrollToContent"
                  >
                    View my Work
                    <v-icon end icon="mdi-arrow-right" class="ml-2"></v-icon>
                  </v-btn>

                  <div class="social-links-row ml-md-6">
                    <v-btn
                      v-for="social in socialLinks"
                      :key="social.name"
                      :icon="social.icon"
                      :href="social.url"
                      target="_blank"
                      variant="text"
                      density="comfortable"
                      class="social-icon-btn mx-1"
                      color="white"
                    ></v-btn>
                  </div>
                </div>
              </div>
            </v-col>

            <!-- Visual/Avatar -->
            <v-col
              cols="12"
              md="5"
              lg="5"
              class="d-none d-md-flex justify-center"
            >
              <div class="hero-visual">
                <div class="avatar-glow"></div>
                <div class="glass-card-backdrop"></div>
                <v-avatar size="380" class="hero-avatar-large">
                  <v-img
                    :src="require('./assets/profile.png')"
                    alt="Bailey Russo"
                    cover
                  ></v-img>
                </v-avatar>

                <!-- Floating badges -->
                <div class="float-badge badge-1">
                  <v-icon color="#61DAFB" class="mr-2">mdi-react</v-icon>
                  <span>React</span>
                </div>
                <div class="float-badge badge-2">
                  <v-icon color="#41B883" class="mr-2">mdi-vuejs</v-icon>
                  <span>Vue</span>
                </div>
                <div class="float-badge badge-3">
                  <v-icon color="#FF9900" class="mr-2">mdi-aws</v-icon>
                  <span>AWS</span>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </section>

      <!-- Skills Section -->
      <section class="skills-section py-16">
        <v-container>
          <v-row justify="center" class="mb-12">
            <v-col cols="12" md="8" class="text-center">
              <h2 class="text-h3 font-weight-bold text-primary mb-4">
                Technical Skills
              </h2>
              <p class="section-subtitle">
                A comprehensive toolset for building digital solutions.
              </p>
            </v-col>
          </v-row>

          <v-row>
            <v-col
              v-for="(category, i) in skillCategories"
              :key="i"
              cols="12"
              md="6"
              lg="4"
              class="mb-6"
            >
              <div class="skill-category-card fill-height pa-6">
                <h3
                  class="text-h5 font-weight-bold text-white mb-6 category-title"
                >
                  {{ category.title }}
                </h3>

                <div class="d-flex flex-wrap gap-3">
                  <div
                    v-for="(skill, index) in category.skills"
                    :key="index"
                    class="skill-pill-dark"
                  >
                    <span class="skill-text-dark">
                      {{ skill }}
                    </span>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </section>

      <!-- Portfolio Section -->
      <section id="content" class="portfolio-section py-16">
        <v-container>
          <!-- Section Header -->
          <v-row justify="center" class="mb-12">
            <v-col cols="12" md="8" class="text-center">
              <h2 class="section-title font-weight-bold mb-4">
                Selected <span class="text-gradient">Projects</span>
              </h2>
              <div class="section-divider mx-auto mb-6"></div>
              <p class="section-subtitle">
                A showcase of my recent technical work and creative endeavors.
              </p>
            </v-col>
          </v-row>

          <!-- Projects Grid -->
          <v-row>
            <v-col
              v-for="project in projects"
              :key="project.id"
              cols="12"
              md="6"
              lg="4"
              class="mb-8"
            >
              <v-hover v-slot="{ isHovering, props }">
                <v-card
                  v-bind="props"
                  class="project-card fill-height"
                  elevation="10"
                  @click="openProjectModal(project)"
                >
                  <!-- Image Wrapper -->
                  <div class="project-image-wrapper">
                    <v-img
                      :src="project.image"
                      height="220"
                      cover
                      class="project-image"
                      :class="{ 'zoom-effect': isHovering }"
                    >
                      <div
                        class="project-overlay"
                        :class="{ 'opacity-1': isHovering }"
                      >
                        <v-btn
                          icon="mdi-arrow-top-right"
                          variant="tonal"
                          color="white"
                          size="large"
                          class="view-btn"
                        ></v-btn>
                      </div>
                    </v-img>
                  </div>

                  <!-- Content -->
                  <div class="project-content pa-6">
                    <div class="d-flex justify-space-between align-start mb-2">
                      <v-chip
                        size="small"
                        color="primary"
                        variant="flat"
                        class="category-chip mb-3"
                      >
                        {{ project.category }}
                      </v-chip>
                    </div>

                    <h3 class="text-h5 font-weight-bold text-white mb-2">
                      {{ project.title }}
                    </h3>

                    <p
                      class="text-body-2 text-grey-lighten-1 mb-4 project-description-truncate"
                    >
                      {{ project.description }}
                    </p>

                    <div class="project-tech-stack">
                      <span
                        v-for="(tech, i) in project.technologies.slice(0, 3)"
                        :key="i"
                        class="tech-dot mr-3"
                      >
                        <span class="dot-color"></span> {{ tech }}
                      </span>
                      <span
                        v-if="project.technologies.length > 3"
                        class="tech-more text-caption"
                      >
                        +{{ project.technologies.length - 3 }} more
                      </span>
                    </div>
                  </div>
                </v-card>
              </v-hover>
            </v-col>
          </v-row>
        </v-container>
      </section>

      <!-- Project Modal -->
      <v-dialog
        v-model="projectModal"
        max-width="900"
        scrollable
        transition="dialog-bottom-transition"
      >
        <v-card class="modal-card">
          <v-btn
            icon="mdi-close"
            variant="text"
            color="white"
            class="modal-close-btn"
            @click="closeProjectModal"
          ></v-btn>

          <v-img
            :src="selectedProject?.image"
            height="400"
            cover
            class="modal-hero-image"
          >
            <div class="modal-hero-overlay">
              <div class="modal-header-content pa-8">
                <v-chip color="primary" variant="flat" class="mb-3">
                  {{ selectedProject?.category }}
                </v-chip>
                <h2 class="text-h3 font-weight-bold text-white">
                  {{ selectedProject?.title }}
                </h2>
              </div>
            </div>
          </v-img>

          <v-card-text class="pa-8 modal-content-body">
            <v-row>
              <v-col cols="12" md="8">
                <h4 class="text-h5 font-weight-bold text-white mb-4">
                  About the Project
                </h4>
                <p
                  class="text-body-1 text-grey-lighten-1 mb-6"
                  style="line-height: 1.8"
                >
                  {{ selectedProject?.description }}
                </p>

                <!-- Placeholder for extended description/case study -->
                <p
                  class="text-body-1 text-grey-lighten-1"
                  style="line-height: 1.8"
                >
                  Designed to solve core user problems with intuitive interfaces
                  and robust backend architecture. Leverages modern design
                  patterns to ensure scalability and performance.
                </p>
              </v-col>

              <v-col cols="12" md="4">
                <div class="tech-sidebar pa-6 rounded-lg">
                  <h4 class="text-h6 font-weight-bold text-white mb-4">
                    Stack
                  </h4>
                  <div class="d-flex flex-wrap gap-2">
                    <v-chip
                      v-for="tech in selectedProject?.technologies"
                      :key="tech"
                      class="mb-2 mr-2"
                      color="white"
                      variant="outlined"
                      size="small"
                    >
                      {{ tech }}
                    </v-chip>
                  </div>

                  <div
                    class="mt-6"
                    v-if="
                      selectedProject?.liveUrl || selectedProject?.githubUrl
                    "
                  >
                    <h4 class="text-h6 font-weight-bold text-white mb-4">
                      Links
                    </h4>
                    <v-btn
                      v-if="selectedProject?.liveUrl"
                      block
                      color="primary"
                      variant="flat"
                      class="mb-3"
                      prepend-icon="mdi-web"
                      :href="selectedProject.liveUrl"
                      target="_blank"
                    >
                      Live Demo
                    </v-btn>
                    <v-btn
                      v-if="selectedProject?.githubUrl"
                      block
                      color="white"
                      variant="outlined"
                      prepend-icon="mdi-github"
                      :href="selectedProject.githubUrl"
                      target="_blank"
                    >
                      View Code
                    </v-btn>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-main>
  </v-app>
</template>

<script>
// Import local images
import IntrepidImage from "./assets/Intrepid.png";
import ReadyVisImage from "./assets/ReadyVis2.png";
import DirectXImage from "./assets/directx.png";
import MapImage from "./assets/map.png";
import MocapImage from "./assets/mocap.png";
import PixelImage from "./assets/pixel.png";
import PoliticalMapImage from "./assets/politicalmap.png";
import WeatherImage from "./assets/weather.png";
import SchoolImage from "./assets/school.png";
import ShelterImage from "./assets/shelter.png";

export default {
  name: "App",
  components: {},
  data() {
    return {
      projectModal: false,
      selectedProject: null,
      socialLinks: [
        {
          name: "LinkedIn",
          icon: "mdi-linkedin",
          url: "https://www.linkedin.com/in/baileyrusso/",
          color: "blue",
        },
        {
          name: "Email",
          icon: "mdi-email",
          url: "mailto:baileyrusso3371@gmail.com",
          color: "red",
        },
      ],
      skillCategories: [
        {
          title: "Languages",
          skills: [
            "JavaScript",
            "TypeScript",
            "Python",
            "C#",
            "C++",
            "PHP",
            "Java",
          ],
        },
        {
          title: "Frontend",
          skills: ["Vue.js", "React", "HTML5", "CSS3", "Tailwind CSS"],
        },
        {
          title: "Backend & Databases",
          skills: ["Node.js", "PostgreSQL", "MySQL", "Redis", "MSSQL"],
        },
        {
          title: "Cloud & DevOps",
          skills: ["AWS", "DigitalOcean", "Git", "Docker"],
        },
        {
          title: "Creative & 3D",
          skills: ["Unity", "Unreal Engine", "Houdini", "Maya"],
        },
      ],
      projects: [
        {
          id: 0,
          title: "Shelter Enterprise",
          category: "Full Stack Development",
          description:
            "Commercial management system for inventory, workflow optimization, and business operations. Features product tracking and utility management capabilities.",
          image: ShelterImage,
          technologies: ["Laravel", "PostgreSQL"],
        },
        {
          id: 1,
          title: "ChalkRecords",
          category: "Full Stack Development",
          description:
            "Classroom management application. Provides a snapshot on how students are doing in real time. Note: Fake Data",
          image: SchoolImage,
          technologies: ["Vue.js", "Node.js", "AWS", "PostgreSQL"],
        },
        {
          id: 2,
          title: "ReadyVis Analytics",
          category: "Data Visualization",
          description:
            "Advanced data visualization, and reporting platform. Used drone footage, and collaborated with civil enegineers to create a 3D visualization of the project. Developed houdini pipelines to automate recreation of the 3D models.",
          image: ReadyVisImage,
          technologies: ["Unreal Engine", "Houdini"],
          liveUrl: "https://example.com",
          githubUrl: "https://github.com/baileyrusso2010",
        },
        {
          id: 3,
          title: "Stock Fighters",
          category: "Full Stack Development",
          description:
            "A game that combines stock trading with competitive gameplay, allowing users to simulate trading strategies in a fun and engaging way.",
          image: IntrepidImage,
          technologies: ["Unity", "AWS", "DynamoDB", "Plaid", "TwelveData"],
          liveUrl: "https://example.com",
          githubUrl: "https://github.com/baileyrusso2010",
        },
        {
          id: 4,
          title: "DirectX Graphics Engine",
          category: "Game Development",
          description: "Recreated flappy bird using C++ and DirectX.",
          image: DirectXImage,
          technologies: ["C++", "DirectX"],
        },

        {
          id: 9,
          title: "Weather Dashboard",
          category: "API Integration",
          description:
            "A comprehensive weather dashboard with location-based forecasts, interactive maps, and personalized weather alerts for multiple cities.",
          image: WeatherImage,
          technologies: ["JavaScript", "OpenWeatherMap", "GeoDB Cities"],
        },
        {
          id: 5,
          title: "Interactive Maps",
          category: "GIS Application",
          description:
            "A dynamic mapping application with real-time location services, custom overlays, and interactive data visualization for geographic information systems.",
          image: MapImage,
          technologies: ["Node.js", "Leaflet", "Vue.js"],
        },
        {
          id: 6,
          title: "Motion Capture Studio",
          category: "Computer Vision",
          description:
            "Motion capture utilizing Kinect, Iclone and Faceware for real-time body tracking and animation generation.",
          image: MocapImage,
          technologies: ["Kinect", "IClone", "Faceware", "Unity"],
        },
        {
          id: 7,
          title: "Pixel Art Creator",
          category: "Creative Tools",
          description:
            "A web-based pixel art creation tool. It allows users to upload image and transform it into pixel art, allowing them to manipulate the pixels for a fun experience.",
          image: PixelImage,
          technologies: ["JavaScript", "CSS"],
        },
        {
          id: 8,
          title: "Political Map Visualization",
          category: "Data Analysis",
          description:
            "An interactive political data visualization platform featuring election results, demographic analysis, and geographic trend mapping.",
          image: PoliticalMapImage,
          technologies: ["ArcGIS", "JavaScript", "CSS"],
        },
      ],
    };
  },
  computed: {
    projectFeatures() {
      // Dynamic features based on project category
      const featuresMap = {
        "Full Stack Development": [
          "Responsive design across all devices",
          "Secure user authentication",
          "Real-time data synchronization",
          "Payment processing integration",
          "Admin dashboard with analytics",
        ],
        "Web Application": [
          "Drag and drop functionality",
          "Real-time collaboration",
          "Progressive Web App features",
          "Offline functionality",
          "Cross-platform compatibility",
        ],
        "Machine Learning": [
          "Natural language processing",
          "Context-aware responses",
          "Machine learning integration",
          "Real-time chat interface",
          "Scalable AI architecture",
        ],
        "Frontend Development": [
          "Modern design principles",
          "Smooth animations and transitions",
          "SEO optimization",
          "Performance optimization",
          "Cross-browser compatibility",
        ],
        "API Integration": [
          "Multiple API integrations",
          "Real-time data updates",
          "Interactive data visualization",
          "Location-based services",
          "Custom notification system",
        ],
        "Data Visualization": [
          "Interactive charts and graphs",
          "Real-time analytics",
          "Custom dashboard creation",
          "Data export functionality",
          "Advanced filtering options",
        ],
        "Data Analysis": [
          "Statistical data processing",
          "Geographic trend mapping",
          "Interactive visualizations",
          "Real-time data filtering",
          "Custom analytics dashboard",
        ],
        "Game Development": [
          "High-performance rendering",
          "Advanced shader programming",
          "3D scene management",
          "Physics simulation",
          "Cross-platform compatibility",
        ],
        "GIS Application": [
          "Real-time location services",
          "Custom map overlays",
          "Geographic data processing",
          "Interactive mapping tools",
          "Spatial analysis features",
        ],
        "Computer Vision": [
          "Real-time motion tracking",
          "Advanced image processing",
          "Machine learning integration",
          "3D pose estimation",
          "Animation generation",
        ],
        "Creative Tools": [
          "Layer-based editing",
          "Animation support",
          "Export functionality",
          "Custom brush tools",
          "Color palette management",
        ],
      };

      return this.selectedProject
        ? featuresMap[this.selectedProject.category] || []
        : [];
    },
  },
  methods: {
    scrollToContent() {
      const element = document.getElementById("content");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    },
    openProjectModal(project) {
      this.selectedProject = project;
      this.projectModal = true;
    },
    closeProjectModal() {
      this.projectModal = false;
      setTimeout(() => {
        this.selectedProject = null;
      }, 300);
    },
  },
};
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Hero Section Styles */
.hero-section {
  min-height: 100vh;
  /* Deep Modern Gradient */
  background: radial-gradient(circle at top right, #2c3e50 0%, #000000 100%);
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.hero-container {
  position: relative;
  z-index: 3;
  padding: 2rem;
}

/* Typography & Content */
.greeting-badge {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  color: #fff;
  font-size: 0.9rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  backdrop-filter: blur(10px);
}

.wave {
  display: inline-block;
  margin-right: 8px;
  animation: wave 2s infinite;
  transform-origin: 70% 70%;
}

@keyframes wave {
  0% {
    transform: rotate(0deg);
  }
  10% {
    transform: rotate(14deg);
  }
  20% {
    transform: rotate(-8deg);
  }
  30% {
    transform: rotate(14deg);
  }
  40% {
    transform: rotate(-4deg);
  }
  50% {
    transform: rotate(10deg);
  }
  60% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(0deg);
  }
}

.hero-title {
  font-size: 5rem !important;
  line-height: 1.1;
  letter-spacing: -2px;
  color: white;
}

.text-gradient {
  background: linear-gradient(120deg, #4facfe 0%, #00f2fe 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.hero-subtitle {
  font-size: 2rem !important;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.4;
}

.highlight {
  color: #4facfe;
  font-weight: 600;
}

.hero-description {
  font-size: 1.2rem !important;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.7);
  max-width: 550px;
  font-weight: 300;
}

/* Buttons */
.hero-btn-primary {
  background: linear-gradient(90deg, #4facfe 0%, #00f2fe 100%) !important;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: none !important;
  transition: all 0.3s ease;
}

.hero-btn-primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(79, 172, 254, 0.4);
}

.social-icon-btn {
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
}

.social-icon-btn:hover {
  background: white;
  color: #2c3e50 !important;
  transform: translateY(-3px);
}

/* Visual Side / Avatar */
.hero-visual {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.hero-avatar-large {
  border: 8px solid rgba(255, 255, 255, 0.1);
  position: relative;
  z-index: 2;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
}

.avatar-glow {
  position: absolute;
  width: 400px;
  height: 400px;
  background: radial-gradient(
    circle,
    rgba(79, 172, 254, 0.4) 0%,
    transparent 70%
  );
  z-index: 1;
  animation: pulse 4s infinite ease-in-out;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 0.6;
  }
}

.glass-card-backdrop {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-6deg);
  width: 320px;
  height: 380px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 40px;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 1;
}

/* Floating Badges */
.float-badge {
  position: absolute;
  background: white;
  padding: 10px 20px;
  border-radius: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 0.9rem;
  z-index: 3;
  animation: floatBadge 6s infinite ease-in-out;
}

.badge-1 {
  top: 10%;
  right: 0;
  animation-delay: 0s;
}
.badge-2 {
  bottom: 15%;
  right: -20px;
  animation-delay: 2s;
}
.badge-3 {
  bottom: 30%;
  left: -20px;
  animation-delay: 4s;
}

@keyframes floatBadge {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-15px);
  }
}

/* Floating Background Shapes */
.floating-shapes {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 0;
}

.shape {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.05),
    rgba(255, 255, 255, 0.01)
  );
  animation: float 20s infinite linear;
}

.shape-1 {
  width: 300px;
  height: 300px;
  top: -100px;
  right: -50px;
}
.shape-2 {
  width: 200px;
  height: 200px;
  bottom: 10%;
  left: 10%;
  animation-delay: -5s;
}
.shape-3 {
  width: 100px;
  height: 100px;
  top: 40%;
  right: 20%;
  animation-delay: -10s;
}

@keyframes float {
  0% {
    transform: rotate(0deg) translate(0, 0);
  }
  50% {
    transform: rotate(180deg) translate(20px, 20px);
  }
  100% {
    transform: rotate(360deg) translate(0, 0);
  }
}

/* Mouse Scroll Indicator */
.scroll-indicator {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
}

.mouse {
  width: 30px;
  height: 50px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-radius: 20px;
  position: relative;
}

.wheel {
  width: 4px;
  height: 8px;
  background: white;
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 2px;
  animation: scrollMouse 2s infinite;
}

@keyframes scrollMouse {
  0% {
    opacity: 1;
    top: 8px;
  }
  100% {
    opacity: 0;
    top: 24px;
  }
}

/* Mobile Adjustments */
@media (max-width: 960px) {
  .hero-title {
    font-size: 3rem !important;
  }
  .hero-subtitle {
    font-size: 1.5rem !important;
  }
  .hero-text-col {
    text-align: center;
  }
  .hero-content {
    align-items: center;
    display: flex;
    flex-direction: column;
  }
  .action-buttons {
    justify-content: center;
  }
  .social-links-row {
    margin-left: 0;
    margin-top: 20px;
  }
}

/* Skills Section */
.skills-section {
  background: #0f172a; /* Match portfolio dark slate */
  position: relative;
  overflow: hidden;
}

.skills-divider {
  height: 4px;
  width: 60px;
  background: linear-gradient(90deg, #4a90e2 0%, #2e86ab 100%);
  border-radius: 2px;
}

.skill-category-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 24px;
  transition: all 0.3s ease;
}

.skill-category-card:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(79, 172, 254, 0.3);
  transform: translateY(-5px);
}

.category-title {
  border-left: 4px solid #4facfe;
  padding-left: 12px;
}

.skill-pill-dark {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  padding: 10px 20px;
  border-radius: 50px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  cursor: default;
}

.skill-pill-dark:hover {
  transform: translateY(-3px);
  background: rgba(79, 172, 254, 0.1);
  border-color: #4facfe;
  box-shadow: 0 5px 15px rgba(79, 172, 254, 0.15);
}

.skill-text-dark {
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0.3px;
}

.skill-pill-dark:hover .skill-text-dark {
  color: #4facfe;
}

/* Portfolio Section */
.portfolio-section {
  background: #0f172a; /* Dark slate */
  position: relative;
}

.section-title {
  font-size: 3rem !important;
  color: white;
}

.section-divider {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #4facfe 0%, #00f2fe 100%);
  border-radius: 2px;
}

.section-subtitle {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.6);
  max-width: 600px;
  margin: 0 auto;
}

/* Project Card */
.project-card {
  background: rgba(30, 41, 59, 0.7) !important; /* Darker glass */
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 24px !important;
  overflow: hidden;
  transition: all 0.4s ease;
  cursor: pointer;
}

.project-card:hover {
  transform: translateY(-8px);
  border-color: rgba(79, 172, 254, 0.5);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4) !important;
}

.project-image-wrapper {
  position: relative;
  overflow: hidden;
}

.project-image {
  transition: transform 0.6s ease;
}

.zoom-effect {
  transform: scale(1.1);
}

.project-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.opacity-1 {
  opacity: 1;
}

.view-btn {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

/* Category Chip */
.category-chip {
  background: rgba(79, 172, 254, 0.15) !important;
  color: #4facfe !important;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.project-description-truncate {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.6;
}

.project-tech-stack {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
}

.tech-dot {
  display: flex;
  align-items: center;
}

.dot-color {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4facfe;
  margin-right: 6px;
  display: inline-block;
}

/* Modal Styles */
.modal-card {
  background: #1e293b !important;
  border-radius: 24px !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
}

.modal-close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(4px);
  border-radius: 50%;
}

.modal-hero-image {
  position: relative;
}

.modal-hero-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background: linear-gradient(to top, #1e293b 0%, transparent 100%);
  padding-top: 100px;
}

.modal-content-body {
  background: #1e293b;
}

.tech-sidebar {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.tech-chip {
  transition: all 0.3s ease;
  font-weight: 500;
  font-size: 0.75rem;
}

.tech-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.project-links {
  border-top: 1px solid #f0f0f0;
  padding-top: 12px;
}

/* Responsive Grid Adjustments */
@media (max-width: 960px) {
  .portfolio-section .v-col {
    margin-bottom: 2rem;
  }
}

@media (max-width: 600px) {
  .project-image-card {
    margin-bottom: 1rem;
  }

  .overlay-buttons {
    gap: 8px;
  }

  .overlay-btn {
    padding: 8px !important;
  }
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px) rotate(0deg);
  }
  33% {
    transform: translateY(-20px) rotate(120deg);
  }
  66% {
    transform: translateY(10px) rotate(240deg);
  }
}

@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-12px);
  }
  60% {
    transform: translateY(-6px);
  }
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .hero-container {
    padding: 3rem 1.5rem;
  }

  .hero-title {
    font-size: 3.5rem !important;
  }

  .hero-subtitle {
    font-size: 1.5rem !important;
  }

  .hero-description {
    font-size: 1.1rem !important;
  }
}

@media (max-width: 600px) {
  .hero-container {
    padding: 2rem 1rem;
  }

  .hero-title {
    font-size: 2.8rem !important;
  }

  .hero-subtitle {
    font-size: 1.3rem !important;
  }

  .hero-description {
    font-size: 1rem !important;
  }

  .action-buttons .v-btn {
    display: block;
    width: 100%;
    margin: 0.75rem 0 !important;
  }

  .hero-avatar {
    width: 120px !important;
    height: 120px !important;
  }

  .social-btn {
    margin: 0 0.5rem !important;
  }
}

/* Project Modal Styles */
.modal-image {
  border-radius: 0;
}

.tech-showcase {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.feature-list {
  background: transparent;
}

.feature-list .v-list-item {
  padding-left: 0;
  min-height: 40px;
}

.feature-list .v-list-item-title {
  font-size: 0.95rem;
  color: #424242;
}

/* Modal Responsive */
@media (max-width: 600px) {
  .v-dialog {
    margin: 12px;
  }

  .modal-image {
    height: 250px !important;
  }
}
</style>
